import { Card, Col, DatePicker, Row, Select, Typography, Space, Button, Statistic, Table, Tabs, Segmented, Tooltip, Progress, Badge, Drawer, Empty } from "antd";
import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import {
    FileTextOutlined,
    CalendarOutlined,
    FilterOutlined,
    EyeOutlined,
    DownloadOutlined,
    BarChartOutlined,
    PieChartOutlined,
    TableOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    RiseOutlined,
    FallOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    ReloadOutlined,
    FullscreenOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';

// Composants existants
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { frenchDateOnly } from "../../../variables/functions";
import { displayNum, displayFloorNum } from "../../../variables/numbersFunctions";
import {
    generateDecaissementsExcel,
    generateDecaissementsHonorairesPDF,
    generateDecaissementsPDF,
    generateRatioSP,
    generateSinistresDeclaresExcel,
    generateSinistresDeclaresPDF,
    generateSinistresSurvenusExcel,
    generateSinistresSurvenusPDF
} from "./etatsSinistresFunctions";
import PreviewModal from "./components/PreviewModal";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const dateFormat = "YYYY-MM-DD";

// ═══════════════════════════════════════════════════════════════
// DESIGN SYSTEM - Thème Moderne avec accents dorés
// ═══════════════════════════════════════════════════════════════
const theme = {
    colors: {
        // Couleurs principales
        primary: '#0f172a',      // Slate 900 - Élégant et professionnel
        secondary: '#1e293b',    // Slate 800
        accent: '#f59e0b',       // Amber 500 - Accent doré
        accentLight: '#fbbf24',  // Amber 400
        
        // États
        success: '#10b981',      // Emerald 500
        warning: '#f59e0b',      // Amber 500
        danger: '#ef4444',       // Red 500
        info: '#3b82f6',         // Blue 500
        
        // Surfaces
        background: '#f8fafc',   // Slate 50
        surface: '#ffffff',
        surfaceHover: '#f1f5f9', // Slate 100
        
        // Texte
        textPrimary: '#0f172a',
        textSecondary: '#64748b', // Slate 500
        textMuted: '#94a3b8',     // Slate 400
        
        // Bordures
        border: '#e2e8f0',       // Slate 200
        borderLight: '#f1f5f9',
    },
    shadows: {
        sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        glow: '0 0 40px rgba(245, 158, 11, 0.15)',
    },
    radius: {
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '24px',
    },
    transitions: {
        fast: 'all 0.15s ease',
        normal: 'all 0.3s ease',
        slow: 'all 0.5s ease',
    }
};

// ═══════════════════════════════════════════════════════════════
// STYLES DES COMPOSANTS
// ═══════════════════════════════════════════════════════════════
const styles = {
    pageContainer: {
        background: theme.colors.background,
        minHeight: '100vh',
    },
    
    // En-tête principal
    header: {
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
        borderRadius: theme.radius.xl,
        padding: '32px',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: theme.shadows.xl,
    },
    headerPattern: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: '50%',
        background: `radial-gradient(circle at 70% 50%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)`,
        pointerEvents: 'none',
    },
    headerContent: {
        position: 'relative',
        zIndex: 1,
    },
    headerTitle: {
        margin: 0,
        color: '#fff',
        fontSize: '28px',
        fontWeight: 700,
        letterSpacing: '-0.5px',
    },
    headerSubtitle: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: '15px',
        marginTop: '8px',
    },
    headerBadge: {
        background: 'rgba(245, 158, 11, 0.2)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        color: theme.colors.accentLight,
        padding: '6px 16px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: 600,
    },
    
    // Cartes de filtres
    filterSection: {
        marginBottom: '24px',
    },
    filterCard: {
        background: theme.colors.surface,
        borderRadius: theme.radius.lg,
        border: `1px solid ${theme.colors.border}`,
        boxShadow: theme.shadows.sm,
        transition: theme.transitions.normal,
        height: '100%',
    },
    filterLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '12px',
    },
    filterIcon: {
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
    },
    
    // Cartes statistiques
    statsGrid: {
        marginBottom: '24px',
    },
    statCard: {
        background: theme.colors.surface,
        borderRadius: theme.radius.lg,
        border: `1px solid ${theme.colors.border}`,
        padding: '24px',
        height: '100%',
        transition: theme.transitions.normal,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
    },
    statCardHover: {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows.lg,
    },
    statIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '22px',
        marginBottom: '16px',
    },
    statValue: {
        fontSize: '28px',
        fontWeight: 700,
        color: theme.colors.textPrimary,
        lineHeight: 1.2,
    },
    statLabel: {
        fontSize: '14px',
        color: theme.colors.textSecondary,
        marginTop: '4px',
    },
    statTrend: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '13px',
        fontWeight: 600,
    },
    
    // Section principale avec tabs
    mainSection: {
        background: theme.colors.surface,
        borderRadius: theme.radius.xl,
        border: `1px solid ${theme.colors.border}`,
        boxShadow: theme.shadows.md,
        overflow: 'hidden',
    },
    tabsHeader: {
        padding: '20px 24px',
        borderBottom: `1px solid ${theme.colors.border}`,
        background: theme.colors.surfaceHover,
    },
    tabsContent: {
        padding: '24px',
    },
    
    // Cartes d'états/rapports
    reportCard: {
        background: theme.colors.surface,
        borderRadius: theme.radius.lg,
        border: `1px solid ${theme.colors.border}`,
        overflow: 'hidden',
        transition: theme.transitions.normal,
        height: '100%',
    },
    reportCardHeader: {
        padding: '20px',
        borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    reportCardIcon: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
    },
    reportCardBody: {
        padding: '20px',
    },
    reportCardFooter: {
        padding: '16px 20px',
        background: theme.colors.surfaceHover,
        borderTop: `1px solid ${theme.colors.border}`,
        display: 'flex',
        gap: '12px',
    },
    
    // Boutons
    btnPrimary: {
        height: '44px',
        borderRadius: '10px',
        fontWeight: 600,
        fontSize: '14px',
        background: theme.colors.primary,
        border: 'none',
        boxShadow: theme.shadows.md,
        transition: theme.transitions.fast,
    },
    btnAccent: {
        height: '44px',
        borderRadius: '10px',
        fontWeight: 600,
        fontSize: '14px',
        background: `linear-gradient(135deg, ${theme.colors.accent} 0%, ${theme.colors.accentLight} 100%)`,
        border: 'none',
        color: theme.colors.primary,
        boxShadow: '0 4px 14px rgba(245, 158, 11, 0.4)',
    },
    btnOutline: {
        height: '40px',
        borderRadius: '10px',
        fontWeight: 500,
        fontSize: '14px',
        background: 'transparent',
        border: `1px solid ${theme.colors.border}`,
        color: theme.colors.textPrimary,
    },
    
    // Table
    tableContainer: {
        borderRadius: theme.radius.md,
        overflow: 'hidden',
        border: `1px solid ${theme.colors.border}`,
    },
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════
const EtatsSinistresModerne = () => {
    // États existants
    const [categories, setCategories] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState("");
    const [selectedEtat, setSelectedEtat] = useState(1);
    const [date, setDate] = useState();
    const [datas, setDatas] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [agences, setAgences] = useState([]);
    const [selectedCompagnie, setSelectedCompagnie] = useState("");
    const [selectedAgence, setSelectedAgence] = useState("");
    const [branche, setBranche] = useState("TPVM");
    
    // États de chargement individuels par type de rapport
    const [loadingStates, setLoadingStates] = useState({
        declares: false,
        survenus: false,
        payes: false,
        honoraires: false,
        ratio: false,
        stats: false,  // Pour le bouton actualiser
    });
    
    // États pour les modales
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
    const [pdfSSPreviewUrl, setPdfSSPreviewUrl] = useState(null);
    const [pdfSDPreviewUrl, setPdfSDPreviewUrl] = useState(null);
    const [pdfHPPreviewUrl, setPdfHPPreviewUrl] = useState(null);
    const [pdfRSPreviewUrl, setPdfRSPreviewUrl] = useState(null);
    
    // Nouveaux états pour l'UI améliorée
    const [activeTab, setActiveTab] = useState('overview');
    const [viewMode, setViewMode] = useState('cards');
    const [previewDrawer, setPreviewDrawer] = useState({ open: false, type: null, data: [] });
    const [stats, setStats] = useState({
        totalSinistres: 0,
        sinistresEnCours: 0,
        sinistresTermines: 0,
        montantTotal: 0,
        ratioSP: 0,
    });
    const [hoveredCard, setHoveredCard] = useState(null);
    
    // Helper pour mettre à jour un état de chargement spécifique
    const setLoading = (type, value) => {
        setLoadingStates(prev => ({ ...prev, [type]: value }));
    };

    // Chargement initial
    useEffect(() => {
        axiosInstance.get(`/api/categorie`)
            .then((res) => setCategories(res.data.filter(e => !e.codeCategorie.startsWith("VT"))))
            .catch(console.log);

        axiosInstance.get(`/api/organisme/assurance`)
            .then((res) => setCompagnies(res.data))
            .catch(console.log);

        axiosInstance.get(`/api/agence/dtrf`)
            .then((res) => setAgences(res.data))
            .catch(console.log);
    }, []);

    // Handlers
    const dateFiltering = (_, dateString) => setDate(dateString);
    const categorieFiltering = (_, categ) => setSelectedCategorie(categ.data);
    const compagnieFiltering = (_, comp) => setSelectedCompagnie(comp.data);
    const agenceFiltering = (_, comp) => setSelectedAgence(comp.data);

    // Fonction pour charger les stats rapides
    const loadQuickStats = async () => {
        if (!date) return;
        setLoading('stats', true);
        try {
            const [declares, survenus, payes, ratio] = await Promise.all([
                axiosInstance.get("/api/sinistre/all", { params: { startDate: date[0], endDate: date[1], categorie: selectedCategorie?.codeCategorie, etat: 4 }}),
                axiosInstance.get("/api/sinistre/all/survenance", { params: { startDate: date[0], endDate: date[1], categorie: selectedCategorie?.codeCategorie, etat: 4 }}),
                axiosInstance.get("/api/decaissement/all-payes", { params: { startDate: date[0], endDate: date[1], categorie: selectedCategorie?.codeCategorie }}),
                axiosInstance.get("/api/stats/ratio-sp", { params: { startDate: date[0], endDate: date[1] }})
            ]);
            
            const totalMontant = payes.data.reduce((acc, item) => acc + (item.montantPaye || item.montant_global || 0), 0);
            const ratioData = ratio.data.reduce((acc, item) => acc + (item.ratioSP || 0), 0) / (ratio.data.length || 1);
            
            setStats({
                totalSinistres: declares.data.length,
                sinistresEnCours: declares.data.filter(s => s.etat === 'I').length,
                sinistresTermines: declares.data.filter(s => s.etat === 'T').length,
                montantTotal: totalMontant,
                ratioSP: ratioData,
            });
        } catch (err) {
            console.error(err);
        }
        setLoading('stats', false);
    };

    // Fonctions de génération avec preview
    const generatePreviewWithData = async (type) => {
        if (!date) {
            toast.error("Veuillez sélectionner une période");
            return;
        }
        setLoading(type, true);
        
        try {
            let endpoint, pdfGenerator, setPreviewUrl;
            
            switch(type) {
                case 'declares':
                    endpoint = "/api/sinistre/all";
                    pdfGenerator = generateSinistresDeclaresPDF;
                    setPreviewUrl = setPdfSDPreviewUrl;
                    break;
                case 'survenus':
                    endpoint = "/api/sinistre/all/survenance";
                    pdfGenerator = generateSinistresSurvenusPDF;
                    setPreviewUrl = setPdfSSPreviewUrl;
                    break;
                case 'payes':
                    endpoint = "/api/decaissement/all-payes";
                    pdfGenerator = generateDecaissementsPDF;
                    setPreviewUrl = setPdfPreviewUrl;
                    break;
                case 'honoraires':
                    endpoint = "/api/decaissement/all-honoraires-payes";
                    pdfGenerator = generateDecaissementsHonorairesPDF;
                    setPreviewUrl = setPdfHPPreviewUrl;
                    break;
                case 'ratio':
                    endpoint = "/api/stats/ratio-sp";
                    pdfGenerator = generateRatioSP;
                    setPreviewUrl = setPdfRSPreviewUrl;
                    break;
                default:
                    setLoading(type, false);
                    return;
            }
            
            const params = type === 'ratio' 
                ? { startDate: date[0], endDate: date[1] }
                : { startDate: date[0], endDate: date[1], categorie: selectedCategorie?.codeCategorie, etat: selectedEtat };
            
            const { data } = await axiosInstance.get(endpoint, { params });
            setDatas(data);
            
            // Ouvrir le drawer de prévisualisation avec les données
            setPreviewDrawer({ open: true, type, data });
            
            // Générer le PDF pour le modal
            const pdfUrl = await pdfGenerator(data, selectedCategorie?.codeCategorie, date);
            setPreviewUrl(pdfUrl);
            
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des données");
        }
        setLoading(type, false);
    };

    // Configuration des colonnes du tableau de prévisualisation
    const getPreviewColumns = (type) => {
        const baseColumns = [
            {
                title: 'N° Sinistre',
                dataIndex: 'numSinistre',
                key: 'numSinistre',
                render: (_, record) => record.numeroSinistreM || record.numeroSinistreP || record.numSinistre || '-',
                width: 140,
            },
            {
                title: 'Nature',
                dataIndex: 'nature',
                key: 'nature',
                width: 100,
            },
            {
                title: 'Date Survenance',
                dataIndex: 'dateSinistre',
                key: 'dateSinistre',
                render: (date) => date ? frenchDateOnly(date) : '-',
                width: 120,
            },
            {
                title: 'Assuré',
                dataIndex: 'nom',
                key: 'nom',
                render: (_, record) => `${record.nom || ''} ${record.prenom || ''}`.trim() || '-',
                ellipsis: true,
            },
            {
                title: 'Statut',
                dataIndex: 'etat',
                key: 'etat',
                width: 100,
                render: (etat) => {
                    const statusConfig = {
                        'I': { color: theme.colors.warning, text: 'En cours', icon: <ClockCircleOutlined /> },
                        'T': { color: theme.colors.success, text: 'Terminé', icon: <CheckCircleOutlined /> },
                        'S': { color: theme.colors.textMuted, text: 'Sans suite', icon: <ExclamationCircleOutlined /> },
                    };
                    const config = statusConfig[etat] || statusConfig['I'];
                    return (
                        <Badge 
                            color={config.color} 
                            text={<span style={{ color: config.color, fontWeight: 500 }}>{config.text}</span>} 
                        />
                    );
                },
            },
        ];
        
        if (type === 'payes' || type === 'honoraires') {
            baseColumns.push({
                title: 'Montant',
                dataIndex: 'montantPaye',
                key: 'montantPaye',
                render: (_, record) => (
                    <span style={{ fontWeight: 600, color: theme.colors.success }}>
                        {displayNum(record.montantPaye || record.montant_global || record.retenue || 0)} FCFA
                    </span>
                ),
                width: 150,
                align: 'right',
            });
        } else {
            baseColumns.push(
                {
                    title: 'Éval. Courante',
                    dataIndex: 'evaluation',
                    key: 'evaluation',
                    render: (val) => <span style={{ fontWeight: 500 }}>{displayNum(val || 0)}</span>,
                    width: 120,
                    align: 'right',
                },
                {
                    title: 'SAP',
                    dataIndex: 'sap',
                    key: 'sap',
                    render: (val) => displayNum(val || 0),
                    width: 100,
                    align: 'right',
                }
            );
        }
        
        return baseColumns;
    };

    // Rendu des cartes de statistiques
    const renderStatCards = () => {
        const statItems = [
            {
                key: 'total',
                label: 'Total Sinistres',
                value: stats.totalSinistres,
                icon: <FileTextOutlined />,
                color: theme.colors.info,
                bgColor: 'rgba(59, 130, 246, 0.1)',
            },
            {
                key: 'encours',
                label: 'En Cours',
                value: stats.sinistresEnCours,
                icon: <ClockCircleOutlined />,
                color: theme.colors.warning,
                bgColor: 'rgba(245, 158, 11, 0.1)',
                trend: { value: 12, up: false },
            },
            {
                key: 'termines',
                label: 'Terminés',
                value: stats.sinistresTermines,
                icon: <CheckCircleOutlined />,
                color: theme.colors.success,
                bgColor: 'rgba(16, 185, 129, 0.1)',
                trend: { value: 8, up: true },
            },
            {
                key: 'montant',
                label: 'Montant Total Payé',
                value: `${displayFloorNum(stats.montantTotal)} F`,
                icon: <BarChartOutlined />,
                color: theme.colors.primary,
                bgColor: 'rgba(15, 23, 42, 0.1)',
            },
        ];

        return (
            <Row gutter={[20, 20]} style={styles.statsGrid}>
                {statItems.map((item) => (
                    <Col xs={24} sm={12} lg={6} key={item.key}>
                        <div
                            style={{
                                ...styles.statCard,
                                ...(hoveredCard === item.key ? styles.statCardHover : {}),
                            }}
                            onMouseEnter={() => setHoveredCard(item.key)}
                            onMouseLeave={() => setHoveredCard(null)}
                        >
                            <div style={{ ...styles.statIcon, background: item.bgColor, color: item.color }}>
                                {item.icon}
                            </div>
                            <div style={styles.statValue}>{item.value}</div>
                            <div style={styles.statLabel}>{item.label}</div>
                            {item.trend && (
                                <div style={{ 
                                    ...styles.statTrend, 
                                    color: item.trend.up ? theme.colors.success : theme.colors.danger 
                                }}>
                                    {item.trend.up ? <RiseOutlined /> : <FallOutlined />}
                                    {item.trend.value}%
                                </div>
                            )}
                        </div>
                    </Col>
                ))}
            </Row>
        );
    };

    // Rendu des cartes de rapports
    const renderReportCards = () => {
        const reports = [
            {
                key: 'declares',
                title: 'Sinistres Déclarés',
                description: 'Liste des sinistres déclarés sur la période sélectionnée',
                icon: <FileTextOutlined />,
                color: theme.colors.info,
                bgColor: 'rgba(59, 130, 246, 0.1)',
            },
            {
                key: 'survenus',
                title: 'Sinistres Survenus',
                description: 'Sinistres survenus dans la période avec dates de survenance',
                icon: <CalendarOutlined />,
                color: theme.colors.warning,
                bgColor: 'rgba(245, 158, 11, 0.1)',
            },
            {
                key: 'payes',
                title: 'Sinistres Payés',
                description: 'Décaissements effectués avec répartition par compagnie',
                icon: <CheckCircleOutlined />,
                color: theme.colors.success,
                bgColor: 'rgba(16, 185, 129, 0.1)',
            },
            {
                key: 'honoraires',
                title: 'Honoraires Payés',
                description: 'Retenues sur honoraires par bénéficiaire',
                icon: <BarChartOutlined />,
                color: theme.colors.accent,
                bgColor: 'rgba(245, 158, 11, 0.15)',
            },
            {
                key: 'ratio',
                title: 'Ratio S/P',
                description: 'Rapport sinistres sur primes par catégorie',
                icon: <PieChartOutlined />,
                color: theme.colors.primary,
                bgColor: 'rgba(15, 23, 42, 0.1)',
            },
        ];

        return (
            <Row gutter={[20, 20]}>
                {reports.map((report) => (
                    <Col xs={24} md={12} xl={8} key={report.key}>
                        <div style={styles.reportCard}>
                            <div style={styles.reportCardHeader}>
                                <div style={{ 
                                    ...styles.reportCardIcon, 
                                    background: report.bgColor, 
                                    color: report.color 
                                }}>
                                    {report.icon}
                                </div>
                                <div>
                                    <Text strong style={{ fontSize: '16px', color: theme.colors.textPrimary }}>
                                        {report.title}
                                    </Text>
                                </div>
                            </div>
                            <div style={styles.reportCardBody}>
                                <Text style={{ color: theme.colors.textSecondary, fontSize: '14px', lineHeight: 1.6 }}>
                                    {report.description}
                                </Text>
                            </div>
                            <div style={styles.reportCardFooter}>
                                <Button
                                    icon={<EyeOutlined />}
                                    style={{ ...styles.btnOutline, flex: 1 }}
                                    onClick={() => generatePreviewWithData(report.key)}
                                    loading={loadingStates[report.key]}
                                >
                                    Aperçu
                                </Button>
                                <Tooltip title="Générer directement le PDF">
                                    <Button
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        style={{ ...styles.btnPrimary, flex: 1 }}
                                        onClick={() => generatePreviewWithData(report.key)}
                                        loading={loadingStates[report.key]}
                                    >
                                        Exporter
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        );
    };

    // Rendu du drawer de prévisualisation
    const renderPreviewDrawer = () => {
        const { open, type, data } = previewDrawer;
        
        const typeLabels = {
            declares: 'Sinistres Déclarés',
            survenus: 'Sinistres Survenus',
            payes: 'Sinistres Payés',
            honoraires: 'Honoraires Payés',
            ratio: 'Ratio S/P',
        };

        const totalMontant = data.reduce((acc, item) => {
            return acc + (item.montantPaye || item.montant_global || item.retenue || item.evaluation || 0);
        }, 0);

        return (
            <Drawer
                title={
                    <Space>
                        <EyeOutlined style={{ color: theme.colors.accent }} />
                        <span>Aperçu - {typeLabels[type]}</span>
                    </Space>
                }
                placement="right"
                width="70vw"
                open={open}
                onClose={() => setPreviewDrawer({ open: false, type: null, data: [] })}
                extra={
                    <Space>
                        <Button 
                            icon={<FilePdfOutlined />}
                            style={styles.btnOutline}
                            onClick={() => {
                                // Ouvrir le modal PDF correspondant
                                setPreviewDrawer({ ...previewDrawer, open: false });
                            }}
                        >
                            Voir PDF
                        </Button>
                        <Button 
                            type="primary"
                            icon={<FileExcelOutlined />}
                            style={{ ...styles.btnPrimary, background: theme.colors.success }}
                            onClick={() => {
                                if (type === 'declares') generateSinistresDeclaresExcel(data, date);
                                else if (type === 'survenus') generateSinistresSurvenusExcel(data, date);
                                else if (type === 'payes') generateDecaissementsExcel(data, selectedCategorie?.codeCategorie, date);
                            }}
                        >
                            Excel
                        </Button>
                    </Space>
                }
                styles={{
                    body: { padding: 0, background: theme.colors.background }
                }}
            >
                {/* Résumé rapide */}
                <div style={{ 
                    padding: '20px 24px', 
                    background: theme.colors.surface,
                    borderBottom: `1px solid ${theme.colors.border}`,
                }}>
                    <Row gutter={24}>
                        <Col span={8}>
                            <Statistic 
                                title="Nombre total" 
                                value={data.length} 
                                valueStyle={{ color: theme.colors.info }}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic 
                                title="Période" 
                                value={date ? `${frenchDateOnly(date[0])} - ${frenchDateOnly(date[1])}` : '-'}
                                valueStyle={{ fontSize: '16px' }}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic 
                                title="Montant total" 
                                value={displayNum(totalMontant)}
                                suffix="FCFA"
                                valueStyle={{ color: theme.colors.success }}
                            />
                        </Col>
                    </Row>
                </div>

                {/* Tableau de données */}
                <div style={{ padding: '24px' }}>
                    {data.length > 0 ? (
                        <div style={styles.tableContainer}>
                            <Table
                                columns={getPreviewColumns(type)}
                                dataSource={data.map((item, idx) => ({ ...item, key: idx }))}
                                pagination={{ 
                                    pageSize: 15,
                                    showSizeChanger: true,
                                    showTotal: (total) => `${total} enregistrements`,
                                }}
                                size="middle"
                                scroll={{ x: 900 }}
                            />
                        </div>
                    ) : (
                        <Empty 
                            description="Aucune donnée disponible pour cette période"
                            style={{ padding: '60px 0' }}
                        />
                    )}
                </div>
            </Drawer>
        );
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                
                <div className="layout-page">
                    <NavBar />
                    
                    <div className="content-wrapper" style={styles.pageContainer}>
                        <div className="container-xxl flex-grow-1 container-p-y">
                            
                            {/* ═══ EN-TÊTE ═══ */}
                            <div style={styles.header}>
                                <div style={styles.headerPattern} />
                                <div style={styles.headerContent}>
                                    <Row justify="space-between" align="middle">
                                        <Col>
                                            <Title level={2} style={styles.headerTitle}>
                                                <FileTextOutlined style={{ marginRight: '14px' }} />
                                                États des Sinistres
                                            </Title>
                                            <Text style={styles.headerSubtitle}>
                                                Visualisez et exportez vos rapports statistiques
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Space>
                                                <span style={styles.headerBadge}>
                                                    <CalendarOutlined style={{ marginRight: '6px' }} />
                                                    {date ? `${frenchDateOnly(date[0])} - ${frenchDateOnly(date[1])}` : 'Aucune période'}
                                                </span>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    style={{
                                                        background: 'rgba(255,255,255,0.1)',
                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                        color: '#fff',
                                                        borderRadius: '10px',
                                                    }}
                                                    onClick={loadQuickStats}
                                                    loading={loadingStates.stats}
                                                >
                                                    Actualiser
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </div>
                            </div>

                            {/* ═══ FILTRES ═══ */}
                            <Row gutter={[16, 16]} style={styles.filterSection}>
                                <Col xs={24} sm={12} lg={6}>
                                    <Card size="small" style={styles.filterCard} bodyStyle={{ padding: '16px' }}>
                                        <div style={styles.filterLabel}>
                                            <div style={{ 
                                                ...styles.filterIcon, 
                                                background: 'rgba(59, 130, 246, 0.1)', 
                                                color: theme.colors.info 
                                            }}>
                                                <CalendarOutlined />
                                            </div>
                                            <Text strong style={{ color: theme.colors.textPrimary }}>Période</Text>
                                        </div>
                                        <RangePicker
                                            format={dateFormat}
                                            onChange={dateFiltering}
                                            placeholder={['Début', 'Fin']}
                                            style={{ width: '100%', borderRadius: '8px' }}
                                        />
                                    </Card>
                                </Col>
                                
                                <Col xs={24} sm={12} lg={6}>
                                    <Card size="small" style={styles.filterCard} bodyStyle={{ padding: '16px' }}>
                                        <div style={styles.filterLabel}>
                                            <div style={{ 
                                                ...styles.filterIcon, 
                                                background: 'rgba(245, 158, 11, 0.1)', 
                                                color: theme.colors.accent 
                                            }}>
                                                <FilterOutlined />
                                            </div>
                                            <Text strong style={{ color: theme.colors.textPrimary }}>Catégorie</Text>
                                        </div>
                                        <Select
                                            placeholder="Toutes les catégories"
                                            style={{ width: '100%' }}
                                            onChange={categorieFiltering}
                                            allowClear
                                            options={categories.map((cat) => ({
                                                value: cat.codeCategorie,
                                                label: cat.nom,
                                                data: cat
                                            }))}
                                        />
                                    </Card>
                                </Col>

                                {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" && branche === "TPVM" && (
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card size="small" style={styles.filterCard} bodyStyle={{ padding: '16px' }}>
                                            <div style={styles.filterLabel}>
                                                <div style={{ 
                                                    ...styles.filterIcon, 
                                                    background: 'rgba(16, 185, 129, 0.1)', 
                                                    color: theme.colors.success 
                                                }}>
                                                    <FilterOutlined />
                                                </div>
                                                <Text strong style={{ color: theme.colors.textPrimary }}>Compagnie</Text>
                                            </div>
                                            <Select
                                                placeholder="Toutes"
                                                style={{ width: '100%' }}
                                                onChange={compagnieFiltering}
                                                allowClear
                                                options={compagnies.map((com) => ({
                                                    value: com.code,
                                                    label: com.raisonSociale,
                                                    data: com
                                                }))}
                                            />
                                        </Card>
                                    </Col>
                                )}

                                <Col xs={24} sm={12} lg={6}>
                                    <Card size="small" style={styles.filterCard} bodyStyle={{ padding: '16px' }}>
                                        <div style={styles.filterLabel}>
                                            <div style={{ 
                                                ...styles.filterIcon, 
                                                background: 'rgba(15, 23, 42, 0.1)', 
                                                color: theme.colors.primary 
                                            }}>
                                                <InfoCircleOutlined />
                                            </div>
                                            <Text strong style={{ color: theme.colors.textPrimary }}>État</Text>
                                        </div>
                                        <Select
                                            value={selectedEtat}
                                            onChange={setSelectedEtat}
                                            style={{ width: '100%' }}
                                            options={[
                                                { label: "En cours", value: 1 },
                                                { label: "Terminé", value: 2 },
                                                { label: "Sans suite", value: 3 },
                                                { label: "Réouverts", value: 5 },
                                                { label: "Tardifs", value: 6 },
                                                { label: "Tous", value: 4 }
                                            ]}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* ═══ STATISTIQUES RAPIDES ═══ */}
                            {date && renderStatCards()}

                            {/* ═══ SECTION PRINCIPALE ═══ */}
                            <div style={styles.mainSection}>
                                <div style={styles.tabsHeader}>
                                    <Row justify="space-between" align="middle">
                                        <Col>
                                            <Segmented
                                                value={activeTab}
                                                onChange={setActiveTab}
                                                options={[
                                                    { label: 'Vue d\'ensemble', value: 'overview', icon: <PieChartOutlined /> },
                                                    { label: 'Rapports', value: 'reports', icon: <FileTextOutlined /> },
                                                ]}
                                                style={{ 
                                                    background: theme.colors.surface,
                                                    borderRadius: '10px',
                                                    padding: '4px',
                                                }}
                                            />
                                        </Col>
                                        <Col>
                                            <Space>
                                                <Segmented
                                                    value={viewMode}
                                                    onChange={setViewMode}
                                                    options={[
                                                        { value: 'cards', icon: <TableOutlined /> },
                                                        { value: 'list', icon: <BarChartOutlined /> },
                                                    ]}
                                                    size="small"
                                                />
                                            </Space>
                                        </Col>
                                    </Row>
                                </div>
                                
                                <div style={styles.tabsContent}>
                                    {activeTab === 'overview' && (
                                        <div>
                                            {!date ? (
                                                <Empty
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                    description={
                                                        <span style={{ color: theme.colors.textSecondary }}>
                                                            Sélectionnez une période pour afficher les statistiques
                                                        </span>
                                                    }
                                                    style={{ padding: '60px 0' }}
                                                >
                                                    <Button 
                                                        type="primary" 
                                                        icon={<CalendarOutlined />}
                                                        style={styles.btnAccent}
                                                    >
                                                        Choisir une période
                                                    </Button>
                                                </Empty>
                                            ) : (
                                                renderReportCards()
                                            )}
                                        </div>
                                    )}
                                    
                                    {activeTab === 'reports' && renderReportCards()}
                                </div>
                            </div>

                            {/* ═══ DRAWER DE PRÉVISUALISATION ═══ */}
                            {renderPreviewDrawer()}

                            {/* ═══ MODALES PDF ═══ */}
                            <PreviewModal
                                visible={!!pdfPreviewUrl}
                                onClose={() => setPdfPreviewUrl(null)}
                                url={pdfPreviewUrl}
                                title="Prévisualisation - Sinistres Payés"
                                downloadName={`Etats_Sinistres_Payes_${date ? frenchDateOnly(date[0]) : ''}_${date ? frenchDateOnly(date[1]) : ''}.pdf`}
                                excelFunction={() => generateDecaissementsExcel(datas, selectedCategorie?.codeCategorie, date)}
                            />

                            <PreviewModal
                                visible={!!pdfSSPreviewUrl}
                                onClose={() => setPdfSSPreviewUrl(null)}
                                url={pdfSSPreviewUrl}
                                title="Prévisualisation - Sinistres Survenus"
                                downloadName={`Etats_Sinistres_Survenus_${date ? frenchDateOnly(date[0]) : ''}_${date ? frenchDateOnly(date[1]) : ''}.pdf`}
                                excelFunction={() => generateSinistresSurvenusExcel(datas, date)}
                            />

                            <PreviewModal
                                visible={!!pdfSDPreviewUrl}
                                onClose={() => setPdfSDPreviewUrl(null)}
                                url={pdfSDPreviewUrl}
                                title="Prévisualisation - Sinistres Déclarés"
                                downloadName={`Etats_Sinistres_Declares_${date ? frenchDateOnly(date[0]) : ''}_${date ? frenchDateOnly(date[1]) : ''}.pdf`}
                                excelFunction={() => generateSinistresDeclaresExcel(datas, date)}
                            />

                            <PreviewModal
                                visible={!!pdfHPPreviewUrl}
                                onClose={() => setPdfHPPreviewUrl(null)}
                                url={pdfHPPreviewUrl}
                                title="Prévisualisation - Honoraires Payés"
                                downloadName={`Etats_Honoraires_Payes_${date ? frenchDateOnly(date[0]) : ''}_${date ? frenchDateOnly(date[1]) : ''}.pdf`}
                                excelFunction={() => generateSinistresDeclaresExcel(datas, date)}
                            />

                            <PreviewModal
                                visible={!!pdfRSPreviewUrl}
                                onClose={() => setPdfRSPreviewUrl(null)}
                                url={pdfRSPreviewUrl}
                                title="Prévisualisation - Ratio S/P"
                                downloadName={`Ratio_SP_${date ? frenchDateOnly(date[0]) : ''}_${date ? frenchDateOnly(date[1]) : ''}.pdf`}
                                excelFunction={null}
                            />

                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EtatsSinistresModerne;